import React from "react";

const apiUrl = process.env.REACT_APP_API_URL;

interface SettingsProps {
  isLoggedIn: boolean;
  isInIframe: boolean;
  setIsInIframe: React.Dispatch<React.SetStateAction<boolean>>;
  showControls: boolean;
  setShowControls: React.Dispatch<React.SetStateAction<boolean>>;
  portalCode: string;
  setPortalCode: React.Dispatch<React.SetStateAction<string>>;
  country: string;
  setCountry: React.Dispatch<React.SetStateAction<string>>;
  language: string;
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
  casinoOperatorId: string;
  setCasinoOperatorId: React.Dispatch<React.SetStateAction<string>>;
  amount: number;
  setAmount: React.Dispatch<React.SetStateAction<number>>;
  client: string;
  setClient: React.Dispatch<React.SetStateAction<string>>;
  closeUrl: string;
  setCloseUrl: React.Dispatch<React.SetStateAction<string>>;
  integrationType: string;
  setIntegrationType: React.Dispatch<React.SetStateAction<string>>;
  userBalance: number;
  setUserBalance: React.Dispatch<React.SetStateAction<number>>;
  bonusBalance: number;
  setBonusBalance: React.Dispatch<React.SetStateAction<number>>;
  bonusStrategy: string;
  setBonusStrategy: React.Dispatch<React.SetStateAction<string>>;
  username: string;
}

const Settings: React.FC<SettingsProps> = ({
  isLoggedIn,
  isInIframe,
  setIsInIframe,
  showControls,
  setShowControls,
  portalCode,
  setPortalCode,
  country,
  setCountry,
  language,
  setLanguage,
  casinoOperatorId,
  setCasinoOperatorId,
  amount,
  setAmount,
  client,
  setClient,
  closeUrl,
  setCloseUrl,
  integrationType,
  setIntegrationType,
  userBalance,
  setUserBalance,
  bonusBalance,
  setBonusBalance,
  bonusStrategy,
  setBonusStrategy,
  username,
}) => {
  // TODO: Hardcoded for now, PS backend should handle integration type logic, based on casino operator id or other parameters
  const integrationTypes = [
    { name: "game2slot" },
    { name: "palmsbet" },
    { name: "gamekong" },
    { name: "betconstruct" },
    { name: "everymatrix" },
    { name: "softswiss" },
  ];

  const bonusStrategyOptions = ["NONE", "BONUS_FIRST", "REAL_FIRST"] as const;

  const setPortal = (portalCode: string) => {
    setPortalCode(portalCode);
    setCasinoOperatorId(portalCode.split("_").slice(0, -1).join("_"));
  };

  const handleBalanceUpdate = () => {
    if (!username) {
      console.error("Error: username is not defined");
      return;
    }

    console.log("Updating balance for user:", username);
    const url = `${apiUrl}/api/update-user-balance`;
    const payload = {
      userId: username,
      amount: userBalance,
      bonusBalance: bonusBalance,
    };

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => console.log("Balance updated:", data))
      .catch((error) => console.error("Error:", error));
  };

  const handleBonusStrategyUpdate = (value: string) => {
    if (!username) {
      console.error("Error: username is not defined");
      return;
    }

    setBonusStrategy(value);

    const url = `${apiUrl}/api/update-bonus-strategy`;
    const payload = {
      userId: username,
      bonusStrategy: value,
    };

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => console.log("Bonus strategy updated:", data))
      .catch((error) => console.error("Error:", error));
  };

  const handleIframeSetting = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsInIframe(e.target.checked);

    if (e.target.checked) {
      setShowControls(true);
    }
  };

  return (
    <div className="settings-section">
      {/* First row of inputs */}
      <div className="input-row">
        <select
          className="styled-input"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          title="Select Language"
        >
          <option value="bg">Български</option>
          <option value="en">English</option>
          <option value="pt">Português</option>
          <option value="pt-br">Português (BR)</option>
          <option value="ro">Română</option>
          <option value="sk">Slovak</option>
        </select>

        {isLoggedIn ? (
          <>
            <input
              type="text"
              placeholder="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="styled-input"
              title="Country code, e.g. bg, en, ru, etc."
            />
            <input
              type="text"
              placeholder="Portal Code"
              value={portalCode}
              onChange={(e) => setPortal(e.target.value)}
              className="styled-input"
              title="Portal code, e.g. PlatformSBet_EUR, PlatformSBet_USD, etc."
            />
            <input
              type="text"
              placeholder="Client"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              className="styled-input"
              title="Client, e.g. DESKTOP, MOBILE, etc."
            />
            <input
              type="text"
              placeholder="Close URL"
              value={closeUrl}
              onChange={(e) => setCloseUrl(e.target.value)}
              className="styled-input"
              title="Close URL, e.g. bbc.com, www.bbc.com, etc."
            />
            <select
              className="styled-input"
              value={integrationType}
              onChange={(e) => setIntegrationType(e.target.value)}
              title="Integration Type"
            >
              <option value="" disabled className="select-label">
                Integration Type:
              </option>
              {integrationTypes.map((type, index) => (
                <option key={index} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <input
              type="text"
              placeholder="Portal Code"
              value={portalCode}
              onChange={(e) => setPortal(e.target.value)}
              className="styled-input"
              title="Portal code, e.g. PlatformSBet_EUR, PlatformSBet_USD, etc."
            />
            <input
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="styled-input"
              title="Amount of money to play with real money - in cents, e.g. 100000 for 1000.00 EUR "
            />
            <input
              type="text"
              placeholder="Client"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              className="styled-input"
              title="Client, e.g. DESKTOP, MOBILE, etc."
            />
            <input
              type="text"
              placeholder="Close URL"
              value={closeUrl}
              onChange={(e) => setCloseUrl(e.target.value)}
              className="styled-input"
              title="Close URL, e.g. bbc.com, www.bbc.com, etc."
            />
            <select
              className="styled-input"
              value={integrationType}
              onChange={(e) => setIntegrationType(e.target.value)}
              title="Integration Type"
            >
              <option value="" disabled className="select-label">
                Integration Type:
              </option>
              {integrationTypes.map((type, index) => (
                <option key={index} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <div className="input-row">
        <input
          type="checkbox"
          checked={isInIframe}
          onChange={(e) => handleIframeSetting(e)}
          title="Open game in iframe or not"
          className="styled-input-checkbox"
        />
        <label htmlFor="isInIframe" className="iframe-label">
          Open in Iframe
        </label>
        <input
          type="checkbox"
          checked={showControls}
          onChange={(e) => setShowControls(e.target.checked)}
          title="Show controls when in iframe"
          className="styled-input-checkbox"
        />
        <label htmlFor="showControls" className="show-controls-label">
          Show Controls
        </label>
      </div>

      {/* Balance Update Section (only if logged in) */}
      {isLoggedIn && (
        <div className="balance-container">
          <div className="balance-input-group">
            <label className="balance-input-label">Balance</label>
            <input
              type="number"
              placeholder="User Balance"
              value={userBalance}
              onChange={(e) => setUserBalance(Number(e.target.value))}
              className="styled-input"
              title="Amount of money to play with real money - in cents, e.g. 100000 for 1000.00 EUR "
            />
          </div>
          <div className="balance-input-group">
            <label className="balance-input-label">Bonus Balance</label>
            <input
              type="number"
              placeholder="Bonus Balance"
              value={bonusBalance}
              onChange={(e) => setBonusBalance(Number(e.target.value))}
              className="styled-input"
              title="Bonus balance amount - in cents, e.g. 100000 for 1000.00 EUR"
            />
          </div>
          <button onClick={handleBalanceUpdate} className="styled-button">
            Update Balance
          </button>
          <div className="balance-input-group">
            <label className="balance-input-label">Bonus Strategy</label>
            <select
              className="styled-input"
              value={bonusStrategy}
              onChange={(e) => handleBonusStrategyUpdate(e.target.value)}
              title="How real vs bonus balance is consumed"
            >
              {bonusStrategyOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
